import React, { useMemo } from 'react';
import { useScratchpadContext } from '../../ScratchpadContext';
import * as ScratchpadHeaderUI from '../ScratchpadHeaderUI';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useFloatingLayerContext } from '../../../FloatingManager';
import { StyledSingleSelectOption } from '../../../Dropdown/SingleSelect';

interface IProps {
  title: string;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isDropdownOpen: boolean) => void;
  onClick: () => void;
}

const ScratchpadSelectMenu = ({
  title,
  isDropdownOpen,
  setIsDropdownOpen,
  onClick,
}: IProps) => {
  const { selectedScratchpad, handleCreateNew, scratchpads, visitScratchpad } =
    useScratchpadContext();

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleSelect = async (scratchpadId: string) => {
    await visitScratchpad(scratchpadId);
    setIsDropdownOpen(false);
  };

  const handleCreateNewClick = () => {
    handleCreateNew();
    setIsDropdownOpen(false);
  };

  const zIndex = useFloatingLayerContext();

  const personalScratchpads = useMemo(() => {
    return scratchpads.filter((scratchpad) => !scratchpad.shared);
  }, [scratchpads]);

  const sharedScratchpads = useMemo(() => {
    return scratchpads.filter((scratchpad) => scratchpad.shared);
  }, [scratchpads]);

  return (
    <>
      <ScratchpadHeaderUI.SelectMenuContainer
        ref={refs.setReference}
        {...getReferenceProps({ onClick })}
      >
        <ScratchpadHeaderUI.Title title={title}>
          {title}
        </ScratchpadHeaderUI.Title>
      </ScratchpadHeaderUI.SelectMenuContainer>

      {isDropdownOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex }}
            {...getFloatingProps()}
          >
            <ScratchpadHeaderUI.Menu>
              <StyledSingleSelectOption
                iconName="plus"
                onClick={handleCreateNewClick}
              >
                New Scratchpad
              </StyledSingleSelectOption>
              {personalScratchpads.map((scratchpad) => (
                <StyledSingleSelectOption
                  key={scratchpad.id}
                  onClick={() => handleSelect(scratchpad.id)}
                  isActive={selectedScratchpad === scratchpad.id}
                >
                  <ScratchpadHeaderUI.TruncatedText>
                    {scratchpad.title}
                  </ScratchpadHeaderUI.TruncatedText>
                </StyledSingleSelectOption>
              ))}
              {sharedScratchpads.length > 0 && (
                <ScratchpadHeaderUI.SharedTitle>
                  Shared with you
                </ScratchpadHeaderUI.SharedTitle>
              )}
              {sharedScratchpads.map((scratchpad) => (
                <StyledSingleSelectOption
                  key={scratchpad.id}
                  onClick={() => handleSelect(scratchpad.id)}
                  isActive={selectedScratchpad === scratchpad.id}
                >
                  <ScratchpadHeaderUI.TruncatedText>
                    {scratchpad.title}
                  </ScratchpadHeaderUI.TruncatedText>
                </StyledSingleSelectOption>
              ))}
            </ScratchpadHeaderUI.Menu>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default ScratchpadSelectMenu;
